import { Container, Text, TextStyle } from 'pixi.js';

interface DamageNumber {
  text: Text;
  startY: number;
  elapsed: number;
  baseScale: number;
}

const FLOAT_DURATION = 1.0;
const FLOAT_DISTANCE = 58;

export class FloatingDamageManager {
  container: Container;
  private numbers: DamageNumber[] = [];
  private style: TextStyle;

  constructor() {
    this.container = new Container();
    this.style = new TextStyle({
      fontFamily: 'Fredoka, system-ui, Arial, sans-serif',
      fontSize: 26,
      fontWeight: '700',
      fill: 0xff5a4a,
      stroke: { color: 0x3a0a06, width: 5 },
      dropShadow: { color: 0x000000, alpha: 0.45, blur: 2, angle: Math.PI / 2, distance: 2 },
    });
  }

  spawn(x: number, y: number, damage: number): void {
    const text = new Text({ text: Math.round(damage).toString(), style: this.style });
    text.anchor.set(0.5, 0.5);
    text.position.set(x, y);
    this.container.addChild(text);

    // Bigger hits show bigger numbers (capped).
    const baseScale = Math.min(1.8, 1 + damage / 120);
    this.numbers.push({
      text,
      startY: y,
      elapsed: 0,
      baseScale,
    });
  }

  update(deltaTime: number): void {
    for (let i = this.numbers.length - 1; i >= 0; i--) {
      const num = this.numbers[i];
      num.elapsed += deltaTime;

      const progress = num.elapsed / FLOAT_DURATION;

      if (progress >= 1) {
        num.text.destroy();
        this.numbers.splice(i, 1);
        continue;
      }

      const easeOut = 1 - Math.pow(1 - progress, 2);
      num.text.position.y = num.startY - FLOAT_DISTANCE * easeOut;
      // Hold opaque, then fade in the last 40% so the number stays readable.
      num.text.alpha = progress > 0.6 ? 1 - (progress - 0.6) / 0.4 : 1;
      // Pop in big, settle to the damage-scaled base size.
      num.text.scale.set(num.baseScale * (1 + 0.35 * (1 - progress)));
    }
  }

  destroy(): void {
    for (const num of this.numbers) {
      num.text.destroy();
    }
    this.numbers = [];
    this.container.destroy({ children: true });
  }
}
